import { Component, effect, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AnalyticsService, IntercomService } from '@/app/core/analytics';
import { AuthService } from '@/app/core/auth/auth.service';
import { ImpersonationBanner, ImpersonationService } from '@/app/core/impersonation';
import { RouteLoading } from '@/app/core/loading';
import { DevThemeToggle } from '@/app/core/theming';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, DevThemeToggle, RouteLoading, ImpersonationBanner],
  host: {
    // Ensure root component fills the entire viewport
    class: 'flex min-h-full w-full flex-auto flex-col',
  },
  // The theme toggle sits OUTSIDE the outlet so it survives every route,
  // signed-out ones included. It renders nothing on production builds.
  // RouteLoading clears the index.html boot splash and shows a top bar while
  // later navigations wait on lazy chunks and guards.
  template: `<ce-route-loading />
    <impersonation-banner />
    <router-outlet />
    <ce-dev-theme-toggle />`,
})
export class App {
  private readonly intercom = inject(IntercomService);
  private readonly analytics = inject(AnalyticsService);
  private readonly auth = inject(AuthService);
  private readonly impersonation = inject(ImpersonationService);

  constructor() {
    // Boot the Intercom support launcher for the whole admin portal, sign-in
    // included. The service existed but was never booted. (CLUB-1930)
    // CLUB-2413: a "view as user" tab must not boot Intercom or GA4 at all, so
    // the target user's identity never reaches either vendor.
    const viewAs = this.impersonation.suppressSideEffects();
    if (!viewAs) {
      this.intercom.boot();
      // GA4, production only; a no-op everywhere else.
      this.analytics.start();
    }
    // Attach the signed-in admin's identity so support has context. Sign-out is
    // handled in AuthService (shutdown before its hard reload), so this only
    // needs to (re)identify on login.
    effect(() => {
      const user = this.auth.user();
      if (this.impersonation.suppressSideEffects()) return;
      this.analytics.setUserId(user?.uid ?? null);
      if (user) {
        this.intercom.registerUser(
          user.uid,
          user.email ?? undefined,
          user.displayName ?? undefined,
        );
      }
    });
  }
}
